// AI Office 助手 API（Sprint 4）· 非串流：回文字 + 建議動作（tool_uses）
import axios from '../../../../api/axios';

export interface OfficeChatMessage { role: 'user' | 'assistant'; content: string }

export interface OfficeAction {
  id: string;
  name: string;   // insert_text / refine_selection / build_docx_section / build_xlsx_table / build_pptx_slide
  input: Record<string, unknown>;
}

export interface OfficeChatResponse {
  usage_log_id: number;   // 4-post-b · evidence Layer 3 outcome report 用
  text: string;
  actions: OfficeAction[];
  stop_reason: string;
  usage: { input_tokens: number; output_tokens: number; total_tokens: number };
}

// 4-post-b · action outcome（evidence Layer 3）
export type OutcomeKind =
  'postmessage_sent' | 'postmessage_failed' | 'kept_by_user' | 'kept_by_timeout' | 'cancelled' | 'route_left';
export interface OutcomeReport {
  action_index: number;
  tool_name: string;
  outcome: OutcomeKind;
  detail?: Record<string, unknown>;
}

export const officeChat = (payload: {
  messages: OfficeChatMessage[];
  mime_type: string;
  filename: string;
  file_object_id?: number;
  selection?: string;
}): Promise<OfficeChatResponse> =>
  axios.post<OfficeChatResponse>('/api/ee/ai/office-chat', payload).then(r => r.data);

export const reportOfficeChatOutcomes = async (usageLogId: number, outcomes: OutcomeReport[]): Promise<void> => {
  try {
    await axios.post(`/api/ee/ai/office-chat/${usageLogId}/outcomes`, { outcomes });
  } catch {
    // silent ok: outcome 回報失敗只是稽核 trail 稍缺，不影響使用者操作
  }
};

// ── Sprint 5-a · Agentic（plan-execute-review + cost transparency）──────────────
export interface PlanStep {
  index: number;
  title: string;
  description: string;
  tools: string[];
  estimated_tokens: number;
}

export interface PlanResponse {
  plan_id: number;
  status: string;
  steps: PlanStep[];
  estimated_total_tokens: number;
  plan_tokens: number;
  plan_cost_usd: number;
  currency: string;
  session: { tokens: number; cost: number };
}

export interface StepDone {
  usage_log_id: number;
  step_index: number;
  tokens: number;
  cost: number;
  currency: string;
  cumulative_tokens: number;
  cumulative_cost: number;
  is_last: boolean;
  billing_reason: string;
}

export interface AbortResult {
  plan_id: number;
  status: string;
  aborted_tokens: number;
  aborted_cost: number;
}

export const createPlan = (payload: {
  original_prompt: string;
  mime_type: string;
  filename: string;
  file_object_id?: number;
  doc_context?: string;
  parent_context?: string;
}): Promise<PlanResponse> =>
  axios.post<PlanResponse>('/api/ee/ai/office-chat/plan', payload).then(r => r.data);

export const confirmPlan = (planId: number): Promise<{ status: string }> =>
  axios.post(`/api/ee/ai/office-chat/plan/${planId}/confirm`).then(r => r.data);

export const abortPlan = (planId: number, reason: 'aborted' | 'cancelled', stepIndex?: number): Promise<AbortResult> =>
  axios.post<AbortResult>(`/api/ee/ai/office-chat/plan/${planId}/abort`,
    { reason, step_index: stepIndex }).then(r => r.data);

export const completePlan = (planId: number): Promise<{ status: string; total_tokens: number; total_cost_usd: number }> =>
  axios.post(`/api/ee/ai/office-chat/plan/${planId}/complete`).then(r => r.data);

export interface StepEventHandlers {
  onStart?: (d: { step_index: number; title: string; reason: string }) => void;
  onDelta?: (text: string) => void;
  onAction?: (a: OfficeAction & { action_index: number }) => void;
  onDone?: (d: StepDone) => void;
  onError?: (d: { code?: string; message?: string }) => void;
}

// EventSource 無法帶 Authorization header → 用 fetch + ReadableStream 讀 SSE（可帶 auth header）。
export const streamStep = async (
  planId: number,
  stepIndex: number,
  body: { reason: 'execute' | 'replan'; feedback?: string },
  handlers: StepEventHandlers,
  signal?: AbortSignal,
): Promise<void> => {
  const base = axios.defaults.baseURL || '';
  const token = localStorage.getItem('session_id') || localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['X-Txn-Token'] = localStorage.getItem('txn_token') || token;
  }
  let resp: Response;
  try {
    resp = await fetch(`${base}/api/ee/ai/office-chat/plan/${planId}/step/${stepIndex}/stream`, {
      method: 'POST', headers, body: JSON.stringify(body), signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    handlers.onError?.({ message: String(e) });
    return;
  }
  if (!resp.ok || !resp.body) {
    handlers.onError?.({ message: `HTTP ${resp.status}` });
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    let chunk;
    try {
      chunk = await reader.read();
    } catch {
      return;   // aborted mid-stream（token 後端已計·中斷屬正常路徑）
    }
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (!data) continue;
      let parsed: Record<string, unknown>;
      try { parsed = JSON.parse(data); } catch { continue; }
      if (event === 'step_start') handlers.onStart?.(parsed as never);
      else if (event === 'step_delta') handlers.onDelta?.(String(parsed.text || ''));
      else if (event === 'action') handlers.onAction?.(parsed as never);
      else if (event === 'step_done') handlers.onDone?.(parsed as never);
      else if (event === 'step_error') handlers.onError?.(parsed as never);
    }
  }
};
